import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { DittoTheme } from '@/lib/dittoTheme';
import { themeToCSSProperties, themeToPolColors, themeToTapeColors, extractImageColors, isVideoBg } from '@/lib/dittoTheme';
import { useDittoTheme } from '@/hooks/useDittoTheme';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface DittoThemeContextType {
  /** The currently active theme (either the user's own or a profile override). */
  activeTheme: DittoTheme | undefined;
  /** Whether the active theme is a profile override (someone else's theme). */
  isOverride: boolean;
  /** Set an override theme (used by profile pages). Pass undefined to clear override entirely. */
  setOverrideTheme: (theme: DittoTheme | undefined) => void;
  /**
   * Force the default theme (no custom theme applied) as an override.
   * Used when visiting a profile that has no theme — we don't want the logged-in
   * user's own theme to bleed through.
   */
  setForceDefault: (force: boolean) => void;
  /** Whether the theme is still loading. */
  isLoading: boolean;
  /** Whether the active theme has a background image (cover/tile). */
  hasBgImage: boolean;
  /** Whether themes are enabled (user toggle). */
  themesEnabled: boolean;
  /** Toggle themes on/off. */
  setThemesEnabled: (enabled: boolean) => void;
  /**
   * Save a theme to localStorage. Works for both logged-in and
   * logged-out users. Logged-out users get the theme applied
   * immediately without publishing to Nostr.
   */
  setLocalTheme: (theme: DittoTheme | undefined) => void;
}

const DittoThemeContext = createContext<DittoThemeContextType>({
  activeTheme: undefined,
  isOverride: false,
  setOverrideTheme: () => {},
  setForceDefault: () => {},
  isLoading: false,
  hasBgImage: false,
  themesEnabled: true,
  setThemesEnabled: () => {},
  setLocalTheme: () => {},
});

export function useDittoThemeContext() {
  return useContext(DittoThemeContext);
}

interface DittoThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Manages Ditto theme application globally.
 * - Fetches the logged-in user's kind 16767 theme and applies it everywhere.
 * - Provides setOverrideTheme() so profile pages can temporarily apply another user's theme.
 * - When the override is cleared, reverts to the logged-in user's theme.
 * - When no theme is set, falls back to the default app theme (CSS :root variables).
 * - Provides a toggle to disable all theme application (reverts to CSS defaults).
 */
export function DittoThemeProvider({ children }: DittoThemeProviderProps) {
  const { user } = useCurrentUser();
  const { data: ownTheme, isLoading: ownLoading } = useDittoTheme(user?.pubkey);
  const [overrideTheme, setOverrideTheme] = useState<DittoTheme | undefined>(undefined);
  const [forceDefault, setForceDefault] = useState(false);
  const [themesEnabled, setThemesEnabled] = useLocalStorage<boolean>('polaroids:themes-enabled', true);
  const [localTheme, setLocalTheme] = useLocalStorage<DittoTheme | undefined>('polaroids:local-theme', undefined);

  const isOverride = overrideTheme !== undefined || forceDefault;

  // Theme resolution order:
  //   1. Override theme (profile pages temporarily apply another user's theme)
  //   2. Locally-saved theme (user explicitly chose a theme on /themes)
  //   3. Logged-in user's Nostr theme (kind 16767, initial load fallback)
  //   4. No theme (CSS defaults)
  // Local theme takes priority over Nostr theme because the user may have
  // just tapped a new theme — the Nostr publish is async and may not have
  // round-tripped yet. On next login the Nostr theme will sync to localStorage.
  // When themes are disabled or forceDefault is set, no theme is applied.
  const baseTheme = localTheme ?? ownTheme;
  const activeTheme = themesEnabled
    ? (forceDefault ? undefined : (overrideTheme !== undefined ? overrideTheme : baseTheme))
    : undefined;
  const hasBgImage = !!(activeTheme?.background);

  const handleSetOverride = useCallback((theme: DittoTheme | undefined) => {
    setOverrideTheme(theme);
    if (theme !== undefined) {
      setForceDefault(false);
    }
  }, []);

  const handleSetForceDefault = useCallback((force: boolean) => {
    setForceDefault(force);
    if (force) {
      setOverrideTheme(undefined);
    }
  }, []);

  const handleSetLocalTheme = useCallback((theme: DittoTheme | undefined) => {
    setLocalTheme(theme);
  }, [setLocalTheme]);

  const contextValue = useMemo(
    () => ({
      activeTheme,
      isOverride,
      setOverrideTheme: handleSetOverride,
      setForceDefault: handleSetForceDefault,
      isLoading: ownLoading,
      hasBgImage,
      themesEnabled,
      setThemesEnabled,
      setLocalTheme: handleSetLocalTheme,
    }),
    [activeTheme, isOverride, handleSetOverride, handleSetForceDefault, ownLoading, hasBgImage, themesEnabled, setThemesEnabled, handleSetLocalTheme],
  );

  return (
    <DittoThemeContext.Provider value={contextValue}>
      <ThemeApplier theme={activeTheme} />
      {children}
    </DittoThemeContext.Provider>
  );
}

/**
 * Handles the side effects of applying a theme:
 * - Sets CSS custom properties on :root
 * - Loads custom fonts via @font-face
 * - Renders a background image/video behind the app
 */
function ThemeApplier({ theme }: { theme: DittoTheme | undefined }) {
  const loadedFontsRef = useRef<Set<string>>(new Set());
  /** Extracted bg-image accent colors, used to enrich tape palette. */
  const [bgImageColors, setBgImageColors] = useState<string[]>([]);
  /** Track which URL we last extracted from to avoid duplicate work. */
  const lastExtractedUrlRef = useRef<string | undefined>(undefined);

  // Extract dominant colors from the background image (async, runs once per URL)
  useEffect(() => {
    const bgUrl = theme?.background?.url;
    const bgMime = theme?.background?.mime;

    // Skip if no bg image, if it's a video, or if we already extracted this URL
    if (!bgUrl || !bgMime || isVideoBg(bgMime) || bgUrl === lastExtractedUrlRef.current) {
      // Clear extracted colors if theme changed and has no bg image
      if (!bgUrl && lastExtractedUrlRef.current) {
        setBgImageColors([]);
        lastExtractedUrlRef.current = undefined;
      }
      return;
    }

    lastExtractedUrlRef.current = bgUrl;

    let cancelled = false;
    extractImageColors(bgUrl).then((colors) => {
      if (!cancelled) setBgImageColors(colors);
    });

    return () => { cancelled = true; };
  }, [theme?.background?.url, theme?.background?.mime]);

  // All tape CSS variable names (for cleanup)
  const TAPE_PROPS = [
    '--tape-0-base', '--tape-0-hi', '--tape-0-solid',
    '--tape-1-base', '--tape-1-hi', '--tape-1-solid',
    '--tape-rainbow-a', '--tape-rainbow-b', '--tape-rainbow-c',
    '--tape-rainbow-d', '--tape-rainbow-e', '--tape-2-solid',
    '--tape-3-base', '--tape-3-solid',
    '--tape-4-base', '--tape-4-hi', '--tape-4-solid',
    '--tape-5-base', '--tape-5-hi', '--tape-5-solid',
    '--tape-6-base', '--tape-6-hi', '--tape-6-solid',
    '--tape-7-base', '--tape-7-hi', '--tape-7-solid',
  ];

  // Apply CSS custom properties
  useEffect(() => {
    const root = document.documentElement;

    if (!theme) {
      // Clean up any previously applied theme properties
      root.removeAttribute('data-ditto-theme');
      const propsToRemove = [
        '--background', '--foreground', '--card', '--card-foreground',
        '--popover', '--popover-foreground', '--primary', '--primary-foreground',
        '--secondary', '--secondary-foreground', '--muted', '--muted-foreground',
        '--accent', '--accent-foreground', '--destructive', '--destructive-foreground',
        '--border', '--input', '--ring',
        '--sidebar-background', '--sidebar-foreground', '--sidebar-primary',
        '--sidebar-primary-foreground', '--sidebar-accent', '--sidebar-accent-foreground',
        '--sidebar-border', '--sidebar-ring',
        '--pol-bg', '--pol-bg-dark', '--pol-white', '--pol-text-color',
        '--pol-text-secondary-color', '--pol-text-caption-color',
        '--pol-primary', '--pol-primary-light',
        ...TAPE_PROPS,
      ];
      for (const prop of propsToRemove) {
        root.style.removeProperty(prop);
      }
      return;
    }

    // Apply shadcn/ui CSS variables
    const cssProps = themeToCSSProperties(theme);
    for (const [prop, value] of Object.entries(cssProps)) {
      root.style.setProperty(prop, value);
    }

    // Apply polaroids-specific CSS variables
    const polColors = themeToPolColors(theme);
    for (const [prop, value] of Object.entries(polColors)) {
      root.style.setProperty(prop, value);
    }

    // Apply theme-derived washi tape colors (enriched with bg-image colors if available)
    const tapeColors = themeToTapeColors(theme, bgImageColors.length > 0 ? bgImageColors : undefined);
    for (const [prop, value] of Object.entries(tapeColors)) {
      root.style.setProperty(prop, value);
    }

    root.setAttribute('data-ditto-theme', 'true');

    return () => {
      root.removeAttribute('data-ditto-theme');
      const allProps = { ...cssProps, ...polColors, ...tapeColors };
      for (const prop of Object.keys(allProps)) {
        root.style.removeProperty(prop);
      }
    };
  }, [theme, bgImageColors]);

  // Load custom fonts
  useEffect(() => {
    if (!theme?.fonts) return;

    const fontsToLoad: { family: string; url: string }[] = [];
    if (theme.fonts.body && !loadedFontsRef.current.has(theme.fonts.body.url)) {
      fontsToLoad.push(theme.fonts.body);
    }
    if (theme.fonts.title && !loadedFontsRef.current.has(theme.fonts.title.url)) {
      fontsToLoad.push(theme.fonts.title);
    }

    for (const font of fontsToLoad) {
      const fontFace = new FontFace(font.family, `url(${font.url})`, {
        display: 'swap',
      });

      fontFace.load().then((loaded) => {
        document.fonts.add(loaded);
        loadedFontsRef.current.add(font.url);
      }).catch((err) => {
        console.warn(`Failed to load font ${font.family}:`, err);
      });
    }
  }, [theme?.fonts]);

  // Apply body font-family override
  useEffect(() => {
    if (!theme?.fonts?.body) {
      document.body.style.removeProperty('font-family');
      return;
    }

    document.body.style.fontFamily = `"${theme.fonts.body.family}", system-ui, sans-serif`;

    return () => {
      document.body.style.removeProperty('font-family');
    };
  }, [theme?.fonts?.body]);

  // Apply background styling to body
  useEffect(() => {
    if (!theme) {
      // Reset to default background
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background-image');
      document.body.style.removeProperty('background-size');
      document.body.style.removeProperty('background-position');
      document.body.style.removeProperty('background-attachment');
      document.body.style.removeProperty('background-repeat');
      return;
    }

    // Always apply the background color
    document.body.style.backgroundColor = theme.colors.background;

    // Apply text color to body
    document.body.style.color = theme.colors.text;

    if (theme.background && !isVideoBg(theme.background.mime)) {
      // Image background
      const { url, mode } = theme.background;
      if (mode === 'cover') {
        document.body.style.backgroundImage = `url(${url})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundAttachment = 'fixed';
        document.body.style.backgroundRepeat = 'no-repeat';
      } else {
        // tile
        document.body.style.backgroundImage = `url(${url})`;
        document.body.style.backgroundSize = 'auto';
        document.body.style.backgroundPosition = 'top left';
        document.body.style.backgroundAttachment = 'scroll';
        document.body.style.backgroundRepeat = 'repeat';
      }
    } else if (!theme.background) {
      // No background image — keep the noise texture from CSS but override the base color.
      // We do NOT remove background-image here because the default CSS applies a noise texture.
      // Instead we let the CSS class handle it. Only set background-color.
      document.body.style.removeProperty('background-image');
      document.body.style.removeProperty('background-size');
      document.body.style.removeProperty('background-position');
      document.body.style.removeProperty('background-attachment');
      document.body.style.removeProperty('background-repeat');
    }

    return () => {
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background-image');
      document.body.style.removeProperty('background-size');
      document.body.style.removeProperty('background-position');
      document.body.style.removeProperty('background-attachment');
      document.body.style.removeProperty('background-repeat');
      document.body.style.removeProperty('color');
    };
  }, [theme]);

  // Render video background if needed
  if (theme?.background && isVideoBg(theme.background.mime)) {
    return (
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <video
          src={theme.background.url}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return null;
}
